import { Direction } from '@providers/Direction';

import { makeSpacing } from './utils/makeSpacing';
import { BorderMixin, borderMixin } from './utils/borderMixin';
import { BreakpointKey, Spacing } from './types';
import { IfMixin, ifMixin } from './utils/makeIfMixin';
import { PaddingMixin, makePaddingMixin } from './utils/makePaddingMixin';
import { ShadowMixin, shadowMixin } from './utils/makeShadowMixin';

// inspired by mui breakpoints:
// https://mui.com/material-ui/customization/breakpoints/#default-breakpoints
type Breakpoints = Record<BreakpointKey, number>;
const DEFAULT_BREAKPOINTS: Breakpoints = {
  xs: 0,
  sm: 480,
  md: 768,
  lg: 1024,
  xl: 1200,
};
const DEFAULT_SPACING_UNIT = 8;
const DEFAULT_COLUMNS_COUNT = 12;

export type Color = {
  50?: string;
  100?: string;
  200?: string;
  300?: string;
  400?: string;
  500?: string;
  600?: string;
  700?: string;
  800?: string;
  900?: string;
};

export type Palette = {
  base: Color;
  grey: Color;
  yellow: Color;
  ocean: Color;
  cool: Color;
  primary: Color;
  success: Color;
  error: Color;
  warning: Color;
};

export type Mixins = {
  padding: PaddingMixin;
  if: IfMixin;
  border: BorderMixin;
  shadow: ShadowMixin;
  // TODO: shadow presets here
};

export type Theme = {
  palette: Palette;
  spacing: Spacing;
  breakpoints: Breakpoints;
  columns: number;
  spacingUnit: number;
  when: (condition: any, styles: object) => object;
  mixins: Mixins;
  screen: {
    width: number;
    height: number;
  };
  direction: Direction;
};

export type BreakpointsInput = Partial<Breakpoints>;

export type ThemeInput = Pick<Theme, 'palette'> & {
  breakpoints?: BreakpointsInput;
  spacingUnit?: number;
  columns?: number;
};

export function createTheme(input: ThemeInput): Theme {
  const spacingUnit = input.spacingUnit ?? DEFAULT_SPACING_UNIT;

  const spacing = makeSpacing(spacingUnit);
  const paddingMixin = makePaddingMixin(spacingUnit);

  const breakpoints: Breakpoints = {
    ...DEFAULT_BREAKPOINTS,
    ...input.breakpoints,
  };

  return {
    palette: input.palette,
    spacing,
    breakpoints,
    columns: input.columns ?? DEFAULT_COLUMNS_COUNT,
    spacingUnit,
    when: (condition: any, styles: object) => {
      return condition ? styles : {};
    },
    mixins: {
      padding: paddingMixin,
      if: ifMixin,
      border: borderMixin,
      shadow: shadowMixin,
    },
    screen: {
      width: 0, // actual values will be sent from computeStyles
      height: 0,
    },
    direction: 'ltr',
  };
}
